import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api } from '@/lib/api'
import { MessageSquare, Filter, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Info } from 'lucide-react'
import type {
  FeedbackRecord,
  FeedbackReply,
  FeedbackReplyPayload,
  FeedbackStatus,
} from '@/types'
import { Button } from '@/components/ui/Button'
import { Textarea } from '@/components/ui/Textarea'
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/Card'

function getErrorMessage(err: unknown, fallback = '操作失败，请稍后再试'): string {
  if (err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message
  }
  return fallback
}

const statusOptions: { label: string; value: FeedbackStatus | 'all' }[] = [
  { label: '全部状态', value: 'all' },
  { label: '待处理', value: 'new' },
  { label: '处理中', value: 'in_review' },
  { label: '已回复', value: 'resolved' },
]

type ToastType = 'success' | 'error' | 'warning' | 'info'

interface ToastState {
  type: ToastType
  title: string
  description?: string
}

function statusLabel(status: FeedbackStatus) {
  switch (status) {
    case 'resolved':
      return '已回复'
    case 'in_review':
      return '处理中'
    case 'new':
    default:
      return '待处理'
  }
}

function statusBadgeClass(status: FeedbackStatus) {
  switch (status) {
    case 'resolved':
      return 'bg-green-100 text-green-700'
    case 'in_review':
      return 'bg-blue-100 text-blue-700'
    case 'new':
    default:
      return 'bg-yellow-100 text-yellow-700'
  }
}

export function FeedbackManagementPage() {
  const [feedbacks, setFeedbacks] = useState<FeedbackRecord[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<FeedbackStatus | 'all'>('all')
  const [replyMessage, setReplyMessage] = useState('')
  const [replyPublic, setReplyPublic] = useState(true)
  const [replyStatus, setReplyStatus] = useState<FeedbackStatus>('resolved')
  const [submittingReply, setSubmittingReply] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [toast, setToast] = useState<ToastState | null>(null)

  useEffect(() => {
    if (!toast) return
    const timer = window.setTimeout(() => setToast(null), 4000)
    return () => window.clearTimeout(timer)
  }, [toast])

  const showToast = (type: ToastType, title: string, description?: string) => {
    setToast({ type, title, description })
  }

  const toastThemes: Record<ToastType, { container: string; icon: ReactNode }> = {
    success: {
      container: 'border-green-200 bg-green-50 text-green-800',
      icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    },
    error: {
      container: 'border-red-200 bg-red-50 text-red-800',
      icon: <XCircle className="h-5 w-5 text-red-600" />,
    },
    warning: {
      container: 'border-amber-200 bg-amber-50 text-amber-800',
      icon: <AlertTriangle className="h-5 w-5 text-amber-600" />,
    },
    info: {
      container: 'border-blue-200 bg-blue-50 text-blue-800',
      icon: <Info className="h-5 w-5 text-blue-500" />,
    },
  }

  const selectedFeedback = useMemo(
    () => feedbacks.find((item) => item.id === selectedId) ?? null,
    [feedbacks, selectedId],
  )

  const totalCount = feedbacks.length
  const pendingCount = useMemo(
    () => feedbacks.filter((item) => item.status === 'new').length,
    [feedbacks]
  )

  const loadFeedbacks = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const items = await api.getFeedbacks({
        status: filterStatus === 'all' ? undefined : filterStatus,
      })
      const sorted = items
        .slice()
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      setFeedbacks(sorted)
      if (sorted.length > 0 && !sorted.some((item) => item.id === selectedId)) {
        setSelectedId(sorted[0].id)
      }
      if (sorted.length === 0) {
        setSelectedId(null)
      }
    } catch (err: unknown) {
      const message = getErrorMessage(err, '加载用户反馈失败')
      setError(message)
      showToast('error', '加载失败', message)
    } finally {
      setLoading(false)
    }
  }, [filterStatus, selectedId])

  useEffect(() => {
    loadFeedbacks()
  }, [loadFeedbacks])

  const handleRefresh = () => {
    loadFeedbacks()
  }

  const handleSelect = (feedback: FeedbackRecord) => {
    setSelectedId(feedback.id)
    setReplyMessage('')
    setReplyPublic(true)
    setReplyStatus('resolved')
  }

  const updateFeedbackEntry = (updated: FeedbackRecord) => {
    setFeedbacks((prev) => prev.map((item) => (item.id === updated.id ? updated : item)))
  }

  const handleReply = async () => {
    if (!selectedFeedback) return
    if (!replyMessage.trim()) {
      const message = '请填写回复内容'
      setError(message)
      showToast('warning', '回复内容为空', message)
      return
    }

    const payload: FeedbackReplyPayload = {
      message: replyMessage.trim(),
      isPublic: replyPublic,
      status: replyStatus,
    }

    setSubmittingReply(true)
    setError(null)
    try {
      const result = await api.replyFeedback(selectedFeedback.id, payload)
      const updated: FeedbackRecord = {
        ...selectedFeedback,
        status: result.status,
        replies: [...selectedFeedback.replies, result.reply],
        lastReplyAt: result.reply.createdAt,
      }
      updateFeedbackEntry(updated)
      setReplyMessage('')
      showToast('success', '回复已发送', replyPublic ? '用户已可查看此回复。' : '仅内部可见的回复已提交。')
    } catch (err: unknown) {
      const message = getErrorMessage(err, '回复失败，请稍后再试')
      setError(message)
      showToast('error', '回复失败', message)
    } finally {
      setSubmittingReply(false)
    }
  }

  const handleStatusUpdate = async (status: FeedbackStatus) => {
    if (!selectedFeedback) return
    setUpdatingStatus(true)
    setError(null)
    try {
      const response = await api.updateFeedbackStatus(selectedFeedback.id, status)
      const updated: FeedbackRecord = {
        ...selectedFeedback,
        status: response.status,
      }
      updateFeedbackEntry(updated)
      showToast('success', '状态已更新', `反馈状态调整为「${statusLabel(response.status)}」。`)
    } catch (err: unknown) {
      const message = getErrorMessage(err, '更新状态失败')
      setError(message)
      showToast('error', '更新状态失败', message)
    } finally {
      setUpdatingStatus(false)
    }
  }

  return (
    <div className="space-y-8">
      {toast && (
        <div
          className={`fixed top-6 right-6 z-50 w-full max-w-sm rounded-2xl border p-4 shadow-lg transition-all duration-200 ${toastThemes[toast.type].container}`}
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5">{toastThemes[toast.type].icon}</div>
            <div className="flex-1">
              <p className="text-sm font-semibold">{toast.title}</p>
              {toast.description && (
                <p className="mt-1 text-xs opacity-80">{toast.description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setToast(null)}
              aria-label="关闭提示"
              className="text-current"
            >
              <XCircle className="h-4 w-4 opacity-60 hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      )}

      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-sky-600 via-indigo-600 to-purple-600 p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-black/5" />
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/20 blur-3xl" />
        <div className="absolute -left-20 -bottom-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
        <div className="relative space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-wider backdrop-blur-sm">
            <MessageSquare className="h-4 w-4" /> 用户反馈管理
          </div>
          <h1 className="text-3xl font-bold lg:text-4xl">收集与回复用户声音</h1>
          <p className="max-w-3xl text-sm text-white/90 lg:text-base">
            快速筛选、查看并处理用户反馈，及时回应问题与建议。
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-600">
          共 <span className="font-semibold text-gray-900">{totalCount}</span> 条反馈 · 待处理
          <span className="mx-1 font-semibold text-amber-600">{pendingCount}</span> 条
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <select
              className="w-full appearance-none rounded-full border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              value={filterStatus}
              onChange={(event) => {
                setFilterStatus(event.target.value as FeedbackStatus | 'all')
              }}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {loading ? '刷新中...' : '刷新列表'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <Card className="border-gray-200 shadow-md">
          <CardHeader className="border-b border-gray-100 bg-gray-50">
            <CardTitle className="text-lg font-semibold text-gray-900">反馈列表</CardTitle>
            <p className="text-sm text-gray-500">
              按提交时间倒序展示最新反馈，点击条目查看详情。
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-hidden rounded-b-xl">
              <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  用户
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  分类
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  内容
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  状态
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  时间
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                    正在加载反馈...
                  </td>
                </tr>
              ) : feedbacks.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                    暂无反馈记录。
                  </td>
                </tr>
              ) : (
                feedbacks.map((feedback) => {
                  const isActive = feedback.id === selectedId
                  return (
                    <tr
                      key={feedback.id}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        isActive ? 'bg-blue-50/50' : ''
                      }`}
                      onClick={() => handleSelect(feedback)}
                    >
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="font-medium text-gray-900">
                          {feedback.username ?? '匿名用户'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {feedback.contact ?? '无联系方式'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="font-medium text-gray-900">{feedback.category}</div>
                        {feedback.rating != null && (
                          <div className="mt-1 flex items-center text-xs text-amber-600">
                            <span className="mr-1">评分</span>
                            <span>{feedback.rating.toFixed(1)}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="line-clamp-2">
                          {feedback.comment ?? '（未填写详情）'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusBadgeClass(feedback.status)}`}>
                          {statusLabel(feedback.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(feedback.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    <Card className="border-gray-200 shadow-md">
      <CardHeader className="border-b border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-gray-900">反馈详情</CardTitle>
            <p className="mt-1 text-sm text-gray-500">查看用户提交的信息并进行回复。</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1">
              <Filter className="h-3.5 w-3.5" /> {statusLabel(filterStatus === 'all' ? 'new' : (filterStatus as FeedbackStatus))}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {selectedFeedback ? (
          <>
            <div className="space-y-3 text-sm text-gray-700">
              <div>
                <span className="font-medium">用户：</span>
                {selectedFeedback.username ?? '匿名用户'}
              </div>
              <div>
                <span className="font-medium">联系：</span>
                {selectedFeedback.contact ?? '未留联系方式'}
              </div>
              <div>
                <span className="font-medium">分类：</span>
                {selectedFeedback.category}
              </div>
              <div>
                <span className="font-medium">提交时间：</span>
                {new Date(selectedFeedback.createdAt).toLocaleString()}
              </div>
              <div>
                <span className="font-medium">详细内容：</span>
                <p className="mt-1 whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-gray-800">
                  {selectedFeedback.comment ?? '（未填写详细描述）'}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">历史回复</h3>
                {selectedFeedback.replies.length > 0 && (
                  <span className="text-xs text-gray-500">
                    最近更新：
                    {selectedFeedback.lastReplyAt
                      ? new Date(selectedFeedback.lastReplyAt).toLocaleString()
                      : '--'}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {selectedFeedback.replies.length === 0 ? (
                  <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-500">
                    暂无管理员回复。
                  </div>
                ) : (
                  selectedFeedback.replies.map((reply) => (
                    <ReplyCard key={reply.id} reply={reply} />
                  ))
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">发送新回复</h3>
                <div className="flex items-center gap-3 text-sm text-gray-600">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={replyPublic}
                      onChange={(event) => setReplyPublic(event.target.checked)}
                    />
                    向用户公开
                  </label>
                  <select
                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm"
                    value={replyStatus}
                    onChange={(event) =>
                      setReplyStatus(event.target.value as FeedbackStatus)
                    }
                  >
                    <option value="resolved">设为已回复</option>
                    <option value="in_review">仍在处理中</option>
                    <option value="new">保持待处理</option>
                  </select>
                </div>
              </div>
              <Textarea
                rows={6}
                placeholder="请输入回复内容"
                value={replyMessage}
                onChange={(event) => setReplyMessage(event.target.value)}
              />
              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setReplyMessage('')}
                  disabled={submittingReply}
                >
                  清空
                </Button>
                <Button onClick={handleReply} loading={submittingReply}>
                  发送回复
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 pt-4 text-sm text-gray-600">
              <div>当前状态：{statusLabel(selectedFeedback.status)}</div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleStatusUpdate('new')}
                  disabled={updatingStatus || selectedFeedback.status === 'new'}
                >
                  设为待处理
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleStatusUpdate('in_review')}
                  disabled={updatingStatus || selectedFeedback.status === 'in_review'}
                >
                  设为处理中
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleStatusUpdate('resolved')}
                  disabled={updatingStatus || selectedFeedback.status === 'resolved'}
                >
                  标记为已回复
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-gray-500">
            <span className="text-base font-medium text-gray-700">未选中反馈</span>
            <span>请选择左侧列表中的反馈记录以查看详情。</span>
          </div>
        )}
      </CardContent>
    </Card>
  </div>
</div>
  )
}

function ReplyCard({ reply }: { reply: FeedbackReply }) {
  return (
    <div className="rounded-md bg-blue-50 p-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="font-medium text-blue-800">{reply.adminName ?? '管理员'}</span>
        <span className="text-xs text-blue-600">
          {new Date(reply.createdAt).toLocaleString()}
        </span>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-blue-900">{reply.message}</p>
      {!reply.isPublic && (
        <div className="mt-2 text-xs text-blue-500">仅内部可见</div>
      )}
    </div>
  )
}
